import { computed, shallowRef } from 'vue'
import type { UserWorkItem } from '@/api/home/type'
import {
  fetchFollowCartoonPage,
  fetchFollowCartoons,
  fetchFollowFeed,
  type FollowCartoonDetailItem,
  type FollowCartoonItem,
} from '@/api/follow'
import { useUserStore } from '@/stores/user'
import type { Response } from '@/types/common'

function mergeUniqueWorks(current: UserWorkItem[], incoming: UserWorkItem[]) {
  const seen = new Set(current.map((item) => item.id))
  const next = current.slice()

  incoming.forEach((item) => {
    if (seen.has(item.id)) {
      return
    }

    seen.add(item.id)
    next.push(item)
  })

  return next
}

function mergeUniqueCartoons(
  current: FollowCartoonDetailItem[],
  incoming: FollowCartoonDetailItem[],
) {
  const byId = new Map(current.map((item) => [item.id, item]))

  incoming.forEach((item) => {
    const existing = byId.get(item.id)

    if (!existing) {
      byId.set(item.id, item)
      return
    }

    byId.set(item.id, {
      ...existing,
      ...item,
      list: mergeUniqueWorks(existing.list ?? [], item.list ?? []),
    })
  })

  return Array.from(byId.values())
}

export function useFollowPage() {
  const userStore = useUserStore()
  const topCartoons = shallowRef<FollowCartoonItem[]>([])
  const feedItems = shallowRef<UserWorkItem[]>([])
  const allFollowedCartoons = shallowRef<FollowCartoonDetailItem[]>([])
  const loadingInitial = shallowRef(false)
  const loadingMore = shallowRef(false)
  const loadingAllCartoons = shallowRef(false)
  const loadingMoreAllCartoons = shallowRef(false)
  const topBarLoading = shallowRef(false)
  const error = shallowRef('')
  const followFeedPageNo = shallowRef(1)
  const followFeedHasNext = shallowRef(true)
  const allCartoonsPageNo = shallowRef(1)
  const allCartoonsHasNext = shallowRef(true)
  const allCartoonsInitialized = shallowRef(false)
  const isAllCartoonsSheetOpen = shallowRef(false)

  const hasFeedItems = computed(() => feedItems.value.length > 0)
  const hasTopCartoons = computed(() => topCartoons.value.length > 0)
  const canLoadMoreFeed = computed(
    () => followFeedHasNext.value && !loadingInitial.value && !loadingMore.value,
  )
  const canLoadMoreAllCartoons = computed(
    () =>
      allCartoonsHasNext.value &&
      allCartoonsInitialized.value &&
      !loadingAllCartoons.value &&
      !loadingMoreAllCartoons.value,
  )

  function reset() {
    topCartoons.value = []
    feedItems.value = []
    allFollowedCartoons.value = []
    error.value = ''
    followFeedPageNo.value = 1
    followFeedHasNext.value = false
    allCartoonsPageNo.value = 1
    allCartoonsHasNext.value = false
    allCartoonsInitialized.value = false
    isAllCartoonsSheetOpen.value = false
  }

  function assertLoggedIn() {
    if (userStore.isLoggedIn()) {
      return true
    }

    reset()
    return false
  }

  function handleResponseCode(response: Response, fallbackMessage: string) {
    if (response.code === 0) {
      return true
    }

    if (response.code === 302) {
      userStore.resetUserInfo()
      reset()
      return false
    }

    throw new Error(response.msg || fallbackMessage)
  }

  async function loadTopCartoons() {
    if (!assertLoggedIn()) {
      return
    }

    topBarLoading.value = true

    try {
      const response = await fetchFollowCartoons()
      if (!handleResponseCode(response, '关注角色加载失败')) {
        return
      }

      topCartoons.value = response.data ?? []
    } finally {
      topBarLoading.value = false
    }
  }

  async function loadFeedPage(pageNo: number, mode: 'replace' | 'append') {
    if (!assertLoggedIn()) {
      return
    }

    const response = await fetchFollowFeed(pageNo)

    if (!handleResponseCode(response, '关注内容加载失败')) {
      return
    }

    const list = response.data?.list ?? []

    if (mode === 'replace') {
      feedItems.value = list
    } else {
      feedItems.value = mergeUniqueWorks(feedItems.value, list)
    }

    followFeedPageNo.value = pageNo + 1
    followFeedHasNext.value = list.length > 0
  }

  async function refresh() {
    if (!assertLoggedIn()) {
      return
    }

    loadingInitial.value = true
    error.value = ''
    followFeedPageNo.value = 1
    followFeedHasNext.value = true
    allFollowedCartoons.value = []
    allCartoonsPageNo.value = 1
    allCartoonsHasNext.value = true
    allCartoonsInitialized.value = false

    try {
      await Promise.all([loadTopCartoons(), loadFeedPage(1, 'replace')])
    } catch (err) {
      error.value = err instanceof Error ? err.message : '关注页加载失败，请稍后重试'
    } finally {
      loadingInitial.value = false
    }
  }

  async function fetchNext() {
    if (!assertLoggedIn() || !canLoadMoreFeed.value) {
      return
    }

    loadingMore.value = true

    try {
      await loadFeedPage(followFeedPageNo.value, 'append')
    } catch (err) {
      error.value = err instanceof Error ? err.message : '加载更多关注内容失败'
      followFeedHasNext.value = false
    } finally {
      loadingMore.value = false
    }
  }

  async function loadAllFollowedCartoons(pageNo: number, mode: 'replace' | 'append') {
    if (!assertLoggedIn()) {
      return
    }

    const response = await fetchFollowCartoonPage(pageNo)

    if (!handleResponseCode(response, '加载关注角色失败')) {
      return
    }

    const page = response.data
    const list = page?.list ?? []

    if (mode === 'replace') {
      allFollowedCartoons.value = list
    } else {
      allFollowedCartoons.value = mergeUniqueCartoons(allFollowedCartoons.value, list)
    }

    allCartoonsPageNo.value = page?.pageNo ?? pageNo + 1
    allCartoonsHasNext.value = Boolean(page?.hasNext)
    allCartoonsInitialized.value = true
  }

  async function openAllCartoonsSheet() {
    if (!assertLoggedIn()) {
      return
    }

    isAllCartoonsSheetOpen.value = true

    if (allCartoonsInitialized.value) {
      return
    }

    loadingAllCartoons.value = true

    try {
      await loadAllFollowedCartoons(1, 'replace')
    } catch (err) {
      error.value = err instanceof Error ? err.message : '加载关注角色失败'
    } finally {
      loadingAllCartoons.value = false
    }
  }

  function closeAllCartoonsSheet() {
    isAllCartoonsSheetOpen.value = false
  }

  async function loadMoreAllCartoons() {
    if (!assertLoggedIn() || !canLoadMoreAllCartoons.value) {
      return
    }

    loadingMoreAllCartoons.value = true

    try {
      await loadAllFollowedCartoons(allCartoonsPageNo.value, 'append')
    } catch (err) {
      error.value = err instanceof Error ? err.message : '加载更多关注角色失败'
      allCartoonsHasNext.value = false
    } finally {
      loadingMoreAllCartoons.value = false
    }
  }

  return {
    topCartoons,
    feedItems,
    allFollowedCartoons,
    loadingInitial,
    loadingMore,
    loadingAllCartoons,
    loadingMoreAllCartoons,
    topBarLoading,
    error,
    hasFeedItems,
    hasTopCartoons,
    canLoadMoreFeed,
    canLoadMoreAllCartoons,
    isAllCartoonsSheetOpen,
    refresh,
    fetchNext,
    openAllCartoonsSheet,
    closeAllCartoonsSheet,
    loadMoreAllCartoons,
  }
}
